import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '@knack/asterisk-react';

import type { KnackFieldKey } from '@/types/schema/KnackField';
import { type ViewRecord } from '@/hooks/api/queries/useViewRecordQuery';
import { cn } from '@/utils/tailwind';
import { getIframeSandboxAttributes, normalizeIframeUrl } from '@/utils/urlValidation';

interface ViewRecordFileFieldProps {
  rawValues: ViewRecord['rawValues'] | undefined;
  fieldKey: KnackFieldKey;
}

// Get file extension and check if it's supported for preview
const getFileExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1).toLowerCase() : '';
};

const unsupportedExtensions = [
  'doc',
  'docx',
  'csv',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'heic',
  'tiff',
  'tif',
  'avi',
  'ogv',
  'mkv',
  '3gp'
];
const imageExtensions = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'bmp',
  'svg',
  'webp',
  'ico',
  'avif',
  'jfif',
  'apng'
];
const audioExtensions = ['wav', 'm4a', 'flac', 'mp3', 'aac', 'ogg', 'oga', 'opus', 'webm'];
const videoExtensions = ['mp4', 'mov', 'webm'];

export function ViewRecordFileField({ rawValues, fieldKey }: ViewRecordFileFieldProps) {
  const [t] = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  if (!rawValues?.[fieldKey]) {
    return '';
  }

  const { signed_url: signedUrl, url, size, filename } = rawValues[fieldKey];
  if (!signedUrl || !filename) {
    return '';
  }

  // Convert bytes to MB and round to 2 decimal places
  const sizeInMB = size ? (size / (1024 * 1024)).toFixed(2) : '0.00';

  // Validate and normalize URL for iframe security
  const safeIframeUrl = normalizeIframeUrl(url);

  const fileExtension = getFileExtension(filename);
  const isUnsupportedFileType = unsupportedExtensions.includes(fileExtension);
  const isImageFile = imageExtensions.includes(fileExtension);
  const isAudioFile = audioExtensions.includes(fileExtension);
  const isVideoFile = videoExtensions.includes(fileExtension);
  const shouldShowIframe = !isUnsupportedFileType && !isImageFile && !isAudioFile && !isVideoFile;
  const shouldUseCompactDialog =
    isUnsupportedFileType || isAudioFile || (shouldShowIframe && !safeIframeUrl);

  const handleLoadError = () => {
    setHasLoadError(true);
  };

  const resetError = () => {
    setHasLoadError(false);
  };

  // Reset error state when dialog opens
  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (open) {
      resetError();
    }
  };

  return (
    <>
      <Button
        intent="secondary"
        size="sm"
        onClick={(e) => {
          // This is to avoid the FileInput (inline dialog) opening when clicking the button
          e.stopPropagation();
          handleDialogOpenChange(true);
        }}
        data-testid="preview-file-button"
      >
        {t('components.views.form.file_field.preview_file')}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
        <Dialog.Content
          className={cn(
            'flex flex-col',
            !shouldUseCompactDialog && 'm-4 h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] !max-w-none'
          )}
          width={isUnsupportedFileType ? 'sm' : undefined}
          aria-describedby={undefined}
          // This is to avoid the FileInput (inline dialog) opening when clicking the button
          onClick={(e) => e.stopPropagation()}
        >
          <Dialog.MainContent
            className={cn('flex flex-col', !isUnsupportedFileType && 'flex-1 overflow-hidden')}
          >
            <Dialog.Header className="flex-shrink-0">
              <Dialog.Title className="overflow-wrap-anywhere break-words">{filename}</Dialog.Title>
            </Dialog.Header>
            <div className={cn('mt-4', !isUnsupportedFileType && 'flex-1 overflow-hidden')}>
              {isUnsupportedFileType && (
                <p data-testid="unsupported-file-message">
                  {t('components.views.form.file_field.unsupported_file_type', {
                    extension: fileExtension
                  })}
                </p>
              )}
              {!isUnsupportedFileType && hasLoadError && (
                <div className="mb-4 rounded border border-destructive bg-destructive/10 p-4">
                  <p className="text-destructive" data-testid="file-load-error-message">
                    {t('components.views.form.file_field.file_load_error')}
                  </p>
                </div>
              )}
              {!isUnsupportedFileType && isImageFile && (
                <img
                  src={url}
                  alt={filename}
                  className="h-full w-full rounded border object-contain"
                  data-testid="file-preview-image"
                  onError={handleLoadError}
                />
              )}
              {!isUnsupportedFileType && isAudioFile && (
                <div className="flex h-full w-full items-center justify-center">
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <audio
                    src={url}
                    controls
                    className="max-w-full"
                    data-testid="file-preview-audio"
                    onError={handleLoadError}
                  >
                    {t('components.views.form.file_field.browser_unsupported')}
                  </audio>
                </div>
              )}
              {!isUnsupportedFileType && isVideoFile && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                  src={url}
                  controls
                  className="h-full w-full rounded border object-contain"
                  data-testid="file-preview-video"
                  onError={handleLoadError}
                >
                  {t('components.views.form.file_field.browser_unsupported')}
                </video>
              )}
              {shouldShowIframe &&
                (!safeIframeUrl ? (
                  <div className="mb-4 rounded border border-destructive bg-destructive/10 p-4">
                    <p className="text-destructive" data-testid="unsafe-url-message">
                      {t('components.views.form.file_field.unsafe_url_error')}
                    </p>
                  </div>
                ) : (
                  <iframe
                    src={safeIframeUrl}
                    title={filename}
                    className="size-full rounded border"
                    data-testid="file-preview-iframe"
                    onError={handleLoadError}
                    sandbox={getIframeSandboxAttributes()}
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                ))}
            </div>
          </Dialog.MainContent>
          <Dialog.Footer className="flex-shrink-0">
            <Dialog.Close asChild>
              <Button intent="minimal" data-testid="close-dialog-button">
                {t('actions.close')}
              </Button>
            </Dialog.Close>
            <Button intent="primary" asChild>
              <a href={signedUrl} download={filename} data-testid="download-file-link">
                {t('components.views.form.file_field.download', { size: sizeInMB })}
              </a>
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
